import { useEffect, useState } from 'react';
import { Loader2, Shield, Send, FileText, Copy, CheckCircle2, ExternalLink, PencilLine, RefreshCw, Handshake } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';
import ManufacturerAgreementDialog from './ManufacturerAgreementDialog';

const STATUS_MAP = {
  to_prepare: { label: 'À préparer', c: 'bg-gray-200 text-gray-700' },
  sent: { label: 'Envoyé', c: 'bg-blue-100 text-blue-700 border border-blue-300' },
  waiting_acceptance: { label: 'En attente de signature', c: 'bg-amber-100 text-amber-800 border border-amber-400' },
  accepted: { label: 'Accepté', c: 'bg-green-100 text-green-700 border border-green-400' },
  refused: { label: 'Refusé', c: 'bg-red-100 text-red-700 border border-red-400' },
  expired: { label: 'Expiré', c: 'bg-gray-200 text-gray-500 border border-gray-300' },
  not_required: { label: 'Non requis', c: 'bg-gray-100 text-gray-500' },
};

/**
 * Per-RFQ mandate manager: shows current status, prepare/send actions, magic link.
 * Displayed inside the RFQ detail block in the admin dashboard.
 */
export default function MandateManager({ rfqId, rfqStatus, clientEmail, onAccepted }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mandate, setMandate] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [maDialog, setMaDialog] = useState(null); // {id, name}

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, mfgRes] = await Promise.all([
        api.get(`/admin/rfq/${rfqId}/mandate`),
        api.get(`/admin/rfq/${rfqId}/manufacturers`),
      ]);
      setMandate(mRes.data);
      setManufacturers(mfgRes.data || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId]);

  const acceptCahier = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/accept-cahier`);
      const msg = res.data?.emailSent ? 'Le client a été notifié par courriel.' : 'Cahier accepté (courriel non envoyé).';
      toast({ title: 'Cahier accepté', description: msg, duration: 10000 });
      if (onAccepted) onAccepted();
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    } finally { setBusy(false); }
  };

  const prepareMandate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rfq/${rfqId}/mandate/prepare`);
      toast({ title: 'Mandat préparé', description: 'Le mandat a été snapshotté à partir de la version courante du modèle.', duration: 8000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    } finally { setBusy(false); }
  };

  const sendMandate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/mandate/send`);
      const msg = res.data?.emailSent ? `Lien envoyé à ${clientEmail}` : 'Lien généré (courriel non envoyé — Resend ?)';
      toast({ title: 'Mandat envoyé', description: msg, duration: 10000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      const res = await api.post(`/admin/rfq/${rfqId}/mandate/send`);
      await navigator.clipboard.writeText(res.data.url);
      toast({ title: 'Lien copié', description: 'Collez-le dans un courriel externe si besoin.', duration: 6000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    }
  };

  const revealIdentity = async (mid) => {
    try {
      await api.post(`/admin/manufacturers/${mid}/reveal-identity`);
      toast({ title: 'Identité révélée', description: 'Le client verra la raison sociale et le site web du manufacturier.', duration: 8000 });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 });
    }
  };
  const hideIdentity = async (mid) => {
    try {
      await api.post(`/admin/manufacturers/${mid}/hide-identity`);
      toast({ title: 'Identité masquée', description: 'Le client ne verra plus le nom du manufacturier.', duration: 6000 });
      load();
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Chargement du mandat…</div>;

  const exists = mandate?.exists;
  const status = mandate?.status || 'to_prepare';
  const info = STATUS_MAP[status] || STATUS_MAP.to_prepare;
  const isAccepted = status === 'accepted';
  const revealed = manufacturers.filter((m) => m.identityRevealed).length;

  return (
    <div className="bg-white border-2 border-brand-navy/20 rounded-lg p-4 space-y-3" data-testid={`mandate-manager-${rfqId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-brand-navy" />
          <h4 className="font-bold text-brand-navy text-sm">Protection de la relation commerciale</h4>
        </div>
        <span data-testid="mandate-admin-status" className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${info.c}`}>{info.label}</span>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <div className="p-2 rounded bg-gray-50">
          <div className="text-[10px] uppercase text-gray-500 font-bold">Statut RFQ</div>
          <div className="text-brand-navy font-semibold mt-0.5">{rfqStatus || 'new'}</div>
        </div>
        <div className="p-2 rounded bg-gray-50">
          <div className="text-[10px] uppercase text-gray-500 font-bold">Manufacturiers présentés</div>
          <div className="text-brand-navy font-bold text-lg">{manufacturers.length}</div>
        </div>
        <div className="p-2 rounded bg-gray-50">
          <div className="text-[10px] uppercase text-gray-500 font-bold">Identités révélées</div>
          <div className="text-brand-navy font-bold text-lg">{revealed} / {manufacturers.length}</div>
        </div>
      </div>

      {mandate?.acceptedAt && (
        <div className="p-2 rounded bg-green-50 border border-green-300 text-xs text-brand-navy flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span>Accepté le {new Date(mandate.acceptedAt).toLocaleString('fr-CA')} par <b>{mandate.signerName}</b> ({mandate.signerCompany}) · méthode : <b>{mandate.method === 'docusign' ? 'DocuSign' : 'interne'}</b> · IP {mandate.signerIP || '—'}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {rfqStatus !== 'cahier_accepte' && rfqStatus !== 'mandate_sent' && rfqStatus !== 'mandate_accepted' && !isAccepted && (
          <button data-testid="accept-cahier-btn" disabled={busy} onClick={acceptCahier} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
            <CheckCircle2 className="w-3.5 h-3.5" /> Valider le cahier des charges
          </button>
        )}
        {!exists && !isAccepted && (
          <button data-testid="prepare-mandate-btn" disabled={busy} onClick={prepareMandate} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-navy text-brand-navy text-xs font-bold hover:bg-brand-navy hover:text-white disabled:opacity-50">
            <PencilLine className="w-3.5 h-3.5" /> Préparer le mandat
          </button>
        )}
        {exists && !isAccepted && (
          <>
            <button data-testid="send-mandate-btn" disabled={busy || !clientEmail} onClick={sendMandate} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs font-bold hover:bg-brand-navy/80 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> Envoyer au client
            </button>
            <button data-testid="copy-mandate-link-btn" onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
              <Copy className="w-3.5 h-3.5" /> Copier le lien client
            </button>
            <button onClick={prepareMandate} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy" title="Re-snapshot avec la version courante du modèle">
              <RefreshCw className="w-3.5 h-3.5" /> Régénérer
            </button>
          </>
        )}
      </div>

      {/* Manufacturers identity reveal panel — only meaningful once mandate accepted */}
      {manufacturers.length > 0 && (
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] uppercase font-bold tracking-widest text-brand-navy">Divulgation d'identité — {manufacturers.length} manufacturiers</div>
            {!isAccepted && <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Nécessite le mandat accepté</span>}
          </div>
          <ul className="space-y-1.5">
            {manufacturers.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1.5 text-xs">
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-brand-navy/70 mr-2">{m.anonymousCode || '—'}</span>
                  <span className="font-semibold text-brand-navy">{m.name}</span>
                  {m.identityRevealed ? (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300 font-bold uppercase">Révélée</span>
                  ) : (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-800 text-white font-bold uppercase">Masquée</span>
                  )}
                  {m.manufacturerProtectionActive && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-brand-yellow/40 border border-brand-yellow text-brand-navy font-bold uppercase" title="Accord manufacturier signé">Accord ✓</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`open-ma-${m.id}`} onClick={() => setMaDialog({ id: m.id, name: m.name })} className="text-[11px] px-2 py-1 rounded border border-gray-300 text-brand-navy hover:border-brand-navy inline-flex items-center gap-1" title="Accord manufacturier">
                    <Handshake className="w-3 h-3" /> Accord
                  </button>
                  {m.identityRevealed ? (
                    <button data-testid={`hide-identity-${m.id}`} onClick={() => hideIdentity(m.id)} className="text-[11px] px-2 py-1 rounded border border-gray-300 text-gray-600 hover:text-red-600 hover:border-red-300">Masquer</button>
                  ) : (
                    <button data-testid={`reveal-identity-${m.id}`} disabled={!isAccepted} onClick={() => revealIdentity(m.id)} className="text-[11px] px-2 py-1 rounded bg-brand-navy text-white font-bold hover:bg-brand-navy/80 disabled:opacity-40 disabled:cursor-not-allowed" title={!isAccepted ? 'Le mandat client doit d\'abord être accepté' : ''}>
                      Révéler
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ManufacturerAgreementDialog
        open={!!maDialog}
        manufacturerId={maDialog?.id}
        manufacturerName={maDialog?.name}
        onClose={() => setMaDialog(null)}
        onChange={load}
      />
    </div>
  );
}
